// Consent gate for analytics. Google Tag Manager (which loads GA4) is NOT
// loaded from index.html anymore — it is injected here ONLY after the visitor
// consents to analytics cookies, satisfying UK/EU opt-in (and India/US).

export const CONSENT_KEY = "agm-cookie-consent-v1";
export const OPEN_SETTINGS_EVENT = "agm:open-cookie-settings";
const GTM_ID = "GTM-PMF8LNLC";
const META_PIXEL_ID = "2060018571611542";

let gtmLoaded = false;
let pixelLoaded = false;

// Read the stored decision, or null if the visitor has not chosen yet.
export function getConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Inject GTM exactly once (idempotent). Called only when analytics is allowed.
export function loadAnalytics() {
  if (gtmLoaded || typeof window === "undefined") return;
  gtmLoaded = true;
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s);
    const dl = l !== "dataLayer" ? "&l=" + l : "";
    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, "script", "dataLayer", GTM_ID);
}

// Inject the Meta (Facebook) Pixel exactly once. Advertising, not analytics —
// it drops the _fbp cookie and reports every page view to Meta — so it hangs
// off the separate `marketing` consent the banner already records, and never
// off `analytics`. Meta's own instructions say to paste this into <head>, which
// would fire it on load for every visitor before anyone had agreed to anything;
// with UK and European devotees on this site that is precisely what the consent
// bar exists to prevent.
//
// Meta's <noscript> tracking pixel is deliberately omitted: a visitor with no
// JavaScript can neither be shown the banner nor record a decision, so firing
// it would be tracking without any possibility of consent.
export function loadMarketing() {
  if (pixelLoaded || typeof window === "undefined") return;
  pixelLoaded = true;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", META_PIXEL_ID);
  window.fbq("track", "PageView");
}

// Persist a decision and load whatever the visitor allowed.
export function saveConsent({ analytics = false, marketing = false }) {
  const value = {
    essential: true,
    analytics: Boolean(analytics),
    marketing: Boolean(marketing),
    ts: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(value));
  } catch {
    /* private mode — consent just won't persist across visits */
  }
  if (value.analytics) loadAnalytics();
  if (value.marketing) loadMarketing();
  return value;
}

// On every page load, re-apply whatever the visitor previously agreed to.
export function applyStoredConsent() {
  const consent = getConsent();
  if (consent?.analytics) loadAnalytics();
  if (consent?.marketing) loadMarketing();
  return consent;
}

// Let the footer "Cookie settings" link reopen the banner.
export function openCookieSettings() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
  }
}
